import { ObjectInfoData } from '../components/dashboard/types';

export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return 'Invalid Date';
  }
};

// Extract relevant fields from potentially inconsistent object info structures
export const extractFields = (data: ObjectInfoData | null | undefined): Record<string, any> => {
  if (!data) return {};

  // Try different paths based on observed API responses
  if (data.status === 'VersionFound' && data.details?.content?.fields) {
    return data.details.content.fields;
  }
  if (data.content?.fields) {
    return data.content.fields;
  }
  if (data.fields) {
    return data.fields;
  }
  if (data.details?.content?.fields) {
    return data.details.content.fields;
  }

  // Handle case where data itself might be the fields object
  // Exclude known metadata/error fields
  const skipFields = ['error', 'message', 'notFound', 'status', 'details', 'content', 'id', 'owner', 'main_object'];
  if (typeof data === 'object' && !Array.isArray(data)) {
    const result: Record<string, any> = {};
    for (const key in data) {
      if (!skipFields.includes(key)) {
        result[key] = data[key];
      }
    }
    if (Object.keys(result).length > 0) {
       return result;
    }
  }

  return {};
};

// Format field values for display
export const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '';

  // New on-chain fields are stored as std::string::String, so the node returns a
  // plain JS string -> pass it through unchanged (no re-encoding).
  if (typeof value === 'string') {
    return value;
  }

  // Old on-chain fields are vector<u8>, returned by the node as a numeric byte
  // array. Decode them as UTF-8 (not String.fromCharCode, which is Latin-1 and
  // mojibakes multi-byte characters like "Café" or emojis).
  if (Array.isArray(value) && value.every(item => typeof item === 'number' && item >= 0 && item <= 255)) {
    try {
      // TextDecoder('utf-8') on an empty array correctly yields '' (empty field).
      return new TextDecoder('utf-8').decode(Uint8Array.from(value));
    } catch (e) {
      console.warn("Failed to decode byte array:", value, e);
      return JSON.stringify(value);
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      console.warn("Failed to stringify object:", value, e);
      return '[Object]'; // Placeholder for complex objects that fail stringify
    }
  }

  return String(value);
};